import { ArrowRightOutlined, ClockCircleTwoTone, InfoCircleFilled, UserOutlined } from '@ant-design/icons';
import type { InputRef } from 'antd';
import { Avatar, Button, ConfigProvider, Input, message, Modal, Select } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { UserInvitation } from '../../../api/organization';
import { claimSpaceOwnership, WorkspaceAccessResponse } from '../../../api/workspace';
import { ExpandDownIcon } from '../../../assets/CustomIcons/ExpandDown';
import { useGetUserFromAccessToken } from '../../../common/auth';
import { getUserInitials } from '../../../common/userHelper';
import { getProfilePictureUrl } from '../../../common/utils';
import colorTokens from '../../../constants/design_system/colors';
import { queryClient } from '../../../queryClient';
import {
  useGetOrganizationInvites,
  useRemoveSpaceAccessFromInvitationMutation,
  useUpdateSpaceRoleInInvitationMutation,
} from '../../../serverState/organization';
import { useRevokeSpaceAccessMutation, useUpdateUserSpaceRoleMutation } from '../../../serverState/user';
import { useGetCurrentWorkspaceUsers, useGetWorkspace } from '../../../serverState/workspace';
import { User } from '../../../types/User';
import { WorkspaceAccessRole } from '../../../types/WorkspaceAccess';
import { WORKSPACE_VISIBILITY } from '../../Onboarding/WorkspaceOnboarding';
import { canInviteUsers, shouldShowClaimOwnership, canManageRoles } from '../../../utils/spacePermissions';
import { InviteToWorkspace } from './InviteToWorkspace';
import { withoutConsumedInvites } from '../pendingInvites';

export type SpaceMembersModalProps = {
  isOpen: boolean;
  onCancel: () => void;
};

export const SpaceMembersModal = ({ isOpen, onCancel }: SpaceMembersModalProps) => {
  const workspaceQuery = useGetWorkspace();
  const workspaceUsersQuery = useGetCurrentWorkspaceUsers();
  const organisationInvitesQuery = useGetOrganizationInvites();

  const [searchValue, setsearchValue] = useState('');
  const searchInputRef = useRef<InputRef>(null);

  // The invite list is cached for 24h (QUERY_CONFIG.STALE_TIME) and nothing invalidates it when
  // someone accepts, so a tab left open keeps serving invitations the server already closed.
  // Refetching on open makes the modal show the current state instead of what was true when the
  // tab first loaded; the effect below then refreshes the member list off the new invite data.
  // (#2988)
  useEffect(() => {
    if (isOpen) {
      void organisationInvitesQuery.refetch();
    }
  }, [isOpen]);

  // Auto-focus search input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Small delay to ensure modal is fully rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const onSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setsearchValue(e.target.value);
  };

  const [filteredUsers, setfilteredUsers] = useState<WorkspaceAccessResponse[]>([]);
  const [filteredInvites, setfilteredInvites] = useState<UserInvitation[]>([]);

  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (workspaceUsersQuery.data) {
      const filtered = workspaceUsersQuery.data
        .filter((workspaceAccess) => {
          const user = workspaceAccess.User as Partial<User>;
          if (user.fullName?.toLowerCase().includes(searchValue.toLowerCase())) {
            return true;
          }
          if (user.email?.toLowerCase().includes(searchValue.toLowerCase())) {
            return true;
          }
          if (workspaceAccess.role?.toLowerCase().includes(searchValue.toLowerCase())) {
            return true;
          }
          return false;
        })
        .sort((a, b) => {
          // 1. Sort by role: owners first, then members
          const getRolePriority = (access: WorkspaceAccessResponse) => {
            return access.role === WorkspaceAccessRole.Owner ? 0 : 1;
          };

          const roleDiff = getRolePriority(a) - getRolePriority(b);
          if (roleDiff !== 0) return roleDiff;

          // 2. Sort by name A-Z (or email if no name)
          const nameA = (a.User.fullName || a.User.email || '').toLowerCase();
          const nameB = (b.User.fullName || b.User.email || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
      setfilteredUsers(filtered);
    }
  }, [searchValue, workspaceUsersQuery.status, workspaceUsersQuery.data]);

  // Refetch workspace users when invites change (e.g., when a pending user accepts)
  useEffect(() => {
    if (organisationInvitesQuery.dataUpdatedAt) {
      workspaceUsersQuery.refetch();
    }
  }, [organisationInvitesQuery.dataUpdatedAt]);

  useEffect(() => {
    if (organisationInvitesQuery.data && workspaceQuery.data) {
      // Drop invitations the membership list has already superseded before anything else: the two
      // queries are independent and can straddle the moment of acceptance, so freshness alone
      // cannot guarantee they agree. (#2988)
      const filtered = withoutConsumedInvites(organisationInvitesQuery.data, workspaceUsersQuery.data ?? [])
        .filter((orgInvite) => {
          // Only show pending invites explicitly tied to this space. Public spaces are opt-in now,
          // so a pending org invite no longer implies membership of every public space.
          const inviteSpaces = orgInvite.Workspaces.find((space) => space.workspaceId === workspaceQuery.data.id);
          if (!inviteSpaces) {
            return false;
          }
          if (orgInvite.email?.toLowerCase().includes(searchValue.toLowerCase())) {
            return true;
          }
          if (orgInvite.invitedAsRole?.toLowerCase().includes(searchValue.toLowerCase())) {
            return true;
          }
          return false;
        })
        .sort((a, b) => {
          // 1. Sort by role: owners first, then members
          const getRolePriority = (invite: UserInvitation) => {
            const spaceRole = invite.Workspaces?.find((space) => space.workspaceId === workspaceQuery.data.id)?.role;
            return spaceRole === WorkspaceAccessRole.Owner ? 0 : 1;
          };

          const roleDiff = getRolePriority(a) - getRolePriority(b);
          if (roleDiff !== 0) return roleDiff;

          // 2. Sort by email A-Z
          const emailA = (a.email || '').toLowerCase();
          const emailB = (b.email || '').toLowerCase();
          return emailA.localeCompare(emailB);
        });

      setfilteredInvites(filtered);
    }
  }, [
    searchValue,
    organisationInvitesQuery.status,
    organisationInvitesQuery.data,
    workspaceQuery.data,
    workspaceUsersQuery.data,
  ]);

  const claimOwnership = async () => {
    const spaceId = workspaceQuery.data?.id;
    if (spaceId) {
      try {
        await claimSpaceOwnership(spaceId);
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: ['workspaceAccess', spaceId],
          });
          queryClient.invalidateQueries({
            queryKey: ['organization'],
          });
        }, 300);
      } catch (error) {
        void messageApi.error(error.message);
      }
    }
  };

  const [isInvitationMode, setisInvitationMode] = useState(false);

  const tokenUserData = useGetUserFromAccessToken();
  const [currentUserSpaceAccess, setcurrentUserSpaceAccess] = useState<WorkspaceAccessResponse | null>(null);
  useEffect(() => {
    if (!workspaceUsersQuery.data) return;
    const usersSpaceAccess = workspaceUsersQuery.data?.find((user) => user.User.id === tokenUserData?.data?.user.id);
    setcurrentUserSpaceAccess(usersSpaceAccess);
  }, [workspaceUsersQuery.data]);

  const showInviteUsers = canInviteUsers(workspaceQuery.data?.visibility, currentUserSpaceAccess?.role);

  const showClaimOwnership = shouldShowClaimOwnership(workspaceUsersQuery.data);

  return (
    <Modal open={isOpen} onCancel={onCancel} footer={null} width={640} centered>
      {contextHolder}
      {!isInvitationMode && (
        <div className="flex flex-col gap-5 pt-3">
          <div className="pl-5 pr-5 flex flex-col gap-5">
            <div className="text-base font-semibold">Team space members</div>
            {showClaimOwnership && (
              <div className="w-full h-8 bg-neutral-200 py-3 flex items-center rounded-lg">
                <InfoCircleFilled className="w-6 h-6 text-yellow-400 ml-5" />
                <div className="text-brand-grey-dark text-sm font-normal leading-tight">
                  Looks like this space currently has no owner.
                </div>
                <ConfigProvider
                  theme={{
                    components: {
                      Button: {
                        colorPrimary: colorTokens.blue[600],
                      },
                    },
                  }}
                >
                  <Button
                    type="primary"
                    className="bg-blue-600 text-white text-sm font-semibold leading-none ml-auto mr-5"
                    onClick={claimOwnership}
                  >
                    Claim ownership
                  </Button>
                </ConfigProvider>
              </div>
            )}
            {showInviteUsers && (
              <div>
                <Input ref={searchInputRef} placeholder="Search people or emails" value={searchValue} onChange={onSearchInput} />
              </div>
            )}
          </div>
          <div className="main-content h-[320px] overflow-y-auto" style={{ boxShadow: 'inset 0 -12px 12px -12px rgba(0, 0, 0, 0.1)' }}>
            {/* {workspaceUsersQuery.data?.map((workspaceAccess) => { */}
            {filteredUsers.map((workspaceAccess) => {
              return (
                <div key={workspaceAccess.id}>
                  <UserInfoRow
                    access={workspaceAccess}
                    currentUserSpaceAccess={currentUserSpaceAccess}
                    messageApi={messageApi}
                  />
                </div>
              );
            })}
            {filteredInvites.map((invitation) => {
              return (
                <div key={invitation.id}>
                  <InvitedUserInfoRow
                    invitation={invitation}
                    currentUserSpaceAccess={currentUserSpaceAccess}
                    messageApi={messageApi}
                  />
                </div>
              );
            })}
            {filteredUsers.length === 0 && filteredInvites.length === 0 && searchValue && (
              <div className="flex flex-col items-center justify-center h-full gap-3">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-neutral-200">
                  <UserOutlined className="text-xl text-neutral-700" />
                </div>
                <span className="text-sm font-medium text-neutral-700">No results found</span>
              </div>
            )}
          </div>

          <div className="flex justify-between">
            <div className="flex space-x-2">
              <InfoCircleFilled style={{ color: colorTokens.neutral[800] }} className="pb-4" />
              <div className="w-80 text-neutral-900 text-xs font-normal font-['Figtree'] leading-none">
                {workspaceQuery.data?.visibility === WORKSPACE_VISIBILITY.Private
                  ? 'Private spaces are only visible to their members. Only space owners can invite users.'
                  : 'Public spaces are discoverable by everyone in your organization, who can join them. Space members can also invite users.'}
              </div>
            </div>

            {showInviteUsers && (
              <Button
                type="primary"
                className="pl-2 pr-2"
                onClick={() => {
                  // TODO: go to invitation mode
                  setisInvitationMode(true);
                }}
              >
                Invite New Members
                <ArrowRightOutlined />
              </Button>
            )}
          </div>
        </div>
      )}

      {isInvitationMode && (
        <InviteToWorkspace
          onBackClick={() => {
            setisInvitationMode(false);
            setsearchValue('');
          }}
          currentUserSpaceAccess={currentUserSpaceAccess}
          onCancel={onCancel}
        />
      )}
    </Modal>
  );
};

export const UserInfoRow = ({
  access,
  currentUserSpaceAccess,
  disableRoleSwitch = false,
  messageApi,
  showRoleAsText = false,
}: {
  access: Partial<WorkspaceAccessResponse>;
  currentUserSpaceAccess: WorkspaceAccessResponse | null;
  disableRoleSwitch?: boolean;
  messageApi?: any;
  showRoleAsText?: boolean;
}) => {
  return (
    <div className="flex justify-between gap-2 items-center pt-2 pb-2 pl-5 pr-5 min-w-0 hover:bg-neutral-200 transition-colors">
      <div className="flex-1 min-w-0">
        <UserInfoBlock user={access.User as User} />
      </div>
      {showRoleAsText ? (
        <span className="text-sm text-neutral-900 pr-3">{access?.role || 'Member'}</span>
      ) : (
        <WorkspaceRoleSwitcher
          access={access}
          currentUserSpaceAccess={currentUserSpaceAccess}
          disabled={disableRoleSwitch}
          messageApi={messageApi}
        />
      )}
    </div>
  );
};

export const InvitedUserInfoRow = ({
  invitation,
  currentUserSpaceAccess,
  messageApi,
}: {
  invitation: Partial<UserInvitation>;
  currentUserSpaceAccess: WorkspaceAccessResponse | null;
  messageApi: any;
}) => {
  return (
    <div className="flex justify-between gap-2 items-center pt-2 pb-2 pl-5 pr-5 min-w-0 hover:bg-neutral-200 transition-colors">
      <div className="flex-1 min-w-0">
        <UserInfoBlock invitation={invitation} />
      </div>
      <InvitationSpaceRoleSwitcher
        invitation={invitation}
        currentUserSpaceAccess={currentUserSpaceAccess}
        messageApi={messageApi}
      />
    </div>
  );
};

interface UserInfoBlockProps {
  user?: Partial<User>;
  invitation?: Partial<UserInvitation>;
}

export const UserInfoBlock: React.FC<UserInfoBlockProps> = ({ user, invitation }) => {
  const isUser = !!user;

  return (
    <div className="w-96 min-w-0">
      <div className="user-info flex justify-start gap-2 min-w-0">
        <div className="flex justify-center items-center flex-shrink-0">
          <Avatar
            className=""
            src={isUser ? (user.profilePicturePath ? getProfilePictureUrl(user) : null) : null}
            icon={!isUser && <UserOutlined className="w-3.5	h-3.5" />}
          >
            {isUser ? getUserInitials(user.fullName || user.email) : ''}
          </Avatar>
        </div>
        {isUser && (
          <div className="flex flex-col min-w-0 flex-1">
            <span className="user-info__name truncate">{user?.fullName ? user.fullName : user?.email}</span>
            <span className="user-info__email truncate">{user?.email}</span>
          </div>
        )}
        {!isUser && (
          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-neutral-900 text-sm font-medium font-['Figtree'] leading-tight truncate">
              {invitation?.email}
            </span>
            <div className=" justify-start items-start inline-flex">
              <div className="bg-blue-100 rounded justify-center items-center gap-1 flex p-1">
                <ClockCircleTwoTone />
                <div className="text-blue-600 text-xs font-normal font-['Figtree'] leading-none">
                  Pending invitation
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const useWorkspaceAccess = () => {
  const workspaceUsersQuery = useGetCurrentWorkspaceUsers();
  const workspaceQuery = useGetWorkspace();
  const userFromAccessTokenQuery = useGetUserFromAccessToken();
  const [currentUsersWorkspaceAccess, setCurrentUsersWorkspaceAccess] = useState<WorkspaceAccessResponse | null>(null);

  useEffect(() => {
    const usersAccess = workspaceUsersQuery.data?.find(
      (access) => access.userId === userFromAccessTokenQuery.data?.user?.id
    );
    setCurrentUsersWorkspaceAccess(usersAccess as WorkspaceAccessResponse);
  }, [workspaceQuery.data, workspaceUsersQuery.data]);

  return {
    currentUsersWorkspaceAccess,
    workspaceUsersQuery,
    workspaceQuery,
    userFromAccessTokenQuery,
  };
};

export const WorkspaceRoleSwitcher = ({
  access,
  currentUserSpaceAccess,
  disabled = false,
  messageApi,
}: {
  access: Partial<WorkspaceAccessResponse>;
  currentUserSpaceAccess: WorkspaceAccessResponse;
  disabled?: boolean;
  messageApi: any;
}) => {
  const { currentUsersWorkspaceAccess } = useWorkspaceAccess();
  const [isOpen, setIsOpen] = useState(false);

  const mutation = useUpdateUserSpaceRoleMutation(access.workspaceId as number);
  const revokeAccessMutation = useRevokeSpaceAccessMutation(access.workspaceId as number);

  const isDisabled = !canManageRoles(currentUsersWorkspaceAccess?.role) || disabled;

  return (
    <Select
      onSelect={(e) => {
        if (e === 'delete') {
          revokeAccessMutation.mutate(
            {
              userEmail: access.User?.email as string,
              spaceId: access.workspaceId as number,
            },
            {
              onError: (error: any) => {
                const errorMessage =
                  typeof error?.response?.data === 'string' ? error?.response?.data : 'Failed to remove user';
                void messageApi.error(errorMessage);
              },
            }
          );
          return;
        }

        if (access?.id) {
          mutation.mutate(
            {
              userEmail: access.User?.email as string,
              spaceId: access.workspaceId as number,
              newRole: e as WorkspaceAccessRole,
            },
            {
              onError: (error: any) => {
                const errorMessage =
                  typeof error?.response?.data === 'string' ? error?.response?.data : 'Failed to update role';
                void messageApi.error(errorMessage);
              },
            }
          );
        }
      }}
      onDropdownVisibleChange={(open) => setIsOpen(open)}
      className={'h-[3em]'}
      value={access?.role || 'Not set'}
      bordered={false}
      disabled={isDisabled}
      suffixIcon={
        isDisabled ? null : (
          <ExpandDownIcon style={{ color: isOpen ? 'inherit' : colorTokens.neutral[900], pointerEvents: 'none' }} />
        )
      }
    >
      <Select.Option value={WorkspaceAccessRole.Owner}>{WorkspaceAccessRole.Owner}</Select.Option>
      <Select.Option value={WorkspaceAccessRole.Member}>{WorkspaceAccessRole.Member}</Select.Option>
      <Select.Option value={'delete'}>
        <div className="text-[#E34935]">Remove</div>
      </Select.Option>
    </Select>
  );
};

export const InvitationSpaceRoleSwitcher = ({
  invitation,
  messageApi,
}: {
  invitation: Partial<UserInvitation>;
  currentUserSpaceAccess: WorkspaceAccessResponse;
  messageApi: any;
}) => {
  const { currentUsersWorkspaceAccess, workspaceQuery } = useWorkspaceAccess();
  const [isOpen, setIsOpen] = useState(false);

  const mutation = useUpdateSpaceRoleInInvitationMutation(invitation?.organizationId as number);
  const removeFromSpaceMutation = useRemoveSpaceAccessFromInvitationMutation(invitation?.organizationId as number);

  const isDisabled = !canManageRoles(currentUsersWorkspaceAccess?.role);

  return (
    <Select
      onSelect={(e) => {
        if (e === 'delete') {
          removeFromSpaceMutation.mutate(
            {
              invitationId: invitation?.id as number,
              spaceId: workspaceQuery.data?.id as number,
            },
            {
              onError: (error: any) => {
                const errorMessage =
                  typeof error?.response?.data === 'string' ? error?.response?.data : 'Failed to remove user';
                void messageApi.error(errorMessage);
              },
            }
          );
          return;
        }

        if (invitation?.id) {
          mutation.mutate(
            {
              invitationId: invitation.id,
              spaceId: workspaceQuery.data?.id as number,
              newRole: e as WorkspaceAccessRole,
            },
            {
              onError: (error: any) => {
                const errorMessage =
                  typeof error?.response?.data === 'string' ? error?.response?.data : 'Failed to update role';
                void messageApi.error(errorMessage);
              },
            }
          );
        }
      }}
      onDropdownVisibleChange={(open) => setIsOpen(open)}
      className={'h-[3em]'}
      value={
        invitation?.Workspaces?.find((space) => space.workspaceId === workspaceQuery.data?.data?.id)?.role ||
        WorkspaceAccessRole.Member
      }
      bordered={false}
      disabled={isDisabled}
      suffixIcon={
        isDisabled ? null : (
          <ExpandDownIcon style={{ color: isOpen ? 'inherit' : colorTokens.neutral[900], pointerEvents: 'none' }} />
        )
      }
    >
      <Select.Option value={WorkspaceAccessRole.Owner}>{WorkspaceAccessRole.Owner}</Select.Option>
      <Select.Option value={WorkspaceAccessRole.Member}>{WorkspaceAccessRole.Member}</Select.Option>
      <Select.Option value={'delete'}>
        <div className="text-[#E34935]">Remove</div>
      </Select.Option>
    </Select>
  );
};
